/**
 * Today's Drill card — the shared daily challenge on the Drills home.
 *
 * One target/day (same for everyone), climbed across three difficulties.
 * Finishing all three advances the streak. The challenge is CAP-EXEMPT,
 * so this card stays active even for capped free users — it's the
 * always-free daily ritual (and a gentle upsell counterpoint: "capped,
 * but you can still do Today's Drill").
 *
 * v1 visuals are intentionally simple — iteration expected.
 */

import { useQuery } from "@tanstack/react-query";

import { ChevronRight, Flame, ICON_STROKE } from "@/shared/components/icons";
import { DrillLaunchLink } from "@/shared/components/DrillLaunchLink";
import { buttonClass } from "@/shared/components/Button";
import { dailyChallengeRoute } from "@/shared/chatRoute";
import {
  DIFFICULTY_LABEL as LEVEL_LABEL,
  getDailyChallenge,
  nextIncompleteDifficulty,
  type DailyChallenge,
} from "@/features/daily/lib/dailyChallenge";
import type { DrillDifficulty } from "@/shared/models";

export function TodaysDrillCard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["daily-challenge"],
    queryFn: getDailyChallenge,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-border bg-surface p-5 text-sm text-text-muted">
        Loading Today's Drill…
      </div>
    );
  }
  // Soft-fail: if the daily endpoint errors, just don't show the card.
  // The rest of the Drills home is unaffected.
  if (error || !data) return null;

  // Completed for the day → no actionable options, so vacate the prime
  // main-column real estate. On desktop the side pane (TodayPanel) shows
  // a compact summary, so render nothing here (lg:hidden). On mobile
  // there is no side pane, so collapse to a slim one-line summary.
  if (data.completedToday) {
    return (
      <div className="lg:hidden rounded-xl border border-accent/30 bg-surface/50 px-4 py-3 flex items-center justify-between gap-3">
        <span className="text-sm text-text-muted">
          <span className="text-accent">✓</span> Today's Drill cleared, back
          tomorrow
        </span>
        <StreakBadge streak={data.currentStreak} />
      </div>
    );
  }

  return <TodaysDrillCardView dc={data} />;
}

function TodaysDrillCardView({ dc }: { dc: DailyChallenge }) {
  const next = nextIncompleteDifficulty(dc);
  const rgb = "122,136,200"; // #7A88C8

  return (
    <section
      className="relative rounded-2xl border border-accent/30 p-5 overflow-hidden"
      style={{
        background: `linear-gradient(135deg, rgba(${rgb},0.16) 0%, rgba(${rgb},0.05) 60%, rgba(${rgb},0.02) 100%)`,
        boxShadow: `0 1px 0 rgba(255,255,255,0.06) inset, 0 24px 48px -28px rgba(${rgb},0.45)`,
      }}
    >
      <div className="relative">
        <div className="flex items-center justify-between mb-2 gap-3">
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.15em]"
            style={{ color: `rgb(${rgb})` }}
          >
            Today's Drill
          </span>
          <StreakBadge streak={dc.currentStreak} />
        </div>

        <div className="font-semibold text-text text-lg tracking-tight">
          {dc.skillName}
        </div>
        <p className="text-sm text-text-muted mt-1 leading-relaxed">
          One skill, three difficulties. Clear all three to keep your streak,
          always free, even at your cap.
        </p>

        <div className="flex flex-wrap gap-2 mt-4">
          {dc.difficulties.map((d) => (
            <Rung
              key={d.level}
              label={LEVEL_LABEL[d.level]}
              done={d.done}
              isNext={d.level === next}
            />
          ))}
        </div>

        <div className="mt-4">
          {next ? (
            <DrillLaunchLink
              softCapExempt
              to={dailyChallengeRoute({
                date: dc.date,
                difficulty: next as DrillDifficulty,
                drillTarget: dc.drillTarget,
                skillName: dc.skillName,
              })}
              className={buttonClass({ variant: "primary" })}
            >
              {dc.difficulties.some((d) => d.done)
                ? `Continue: ${LEVEL_LABEL[next]}`
                : "Start Today's Drill"}
              <ChevronRight size={14} strokeWidth={ICON_STROKE} />
            </DrillLaunchLink>
          ) : (
            <div className="inline-flex items-center gap-2 px-4 py-3 rounded-lg border border-accent/30 bg-accent/10 text-sm text-text font-medium">
              <span aria-hidden>✓</span>
              Done for today, back tomorrow
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Rung({
  label,
  done,
  isNext,
}: {
  label: string;
  done: boolean;
  isNext: boolean;
}) {
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition",
        done
          ? "border-accent/40 bg-accent/15 text-accent"
          : isNext
            ? "border-accent/40 bg-surface text-text"
            : "border-border bg-surface/50 text-text-muted/70",
      ].join(" ")}
    >
      <span aria-hidden className="text-[11px]">
        {done ? "✓" : isNext ? "●" : "○"}
      </span>
      {label}
    </span>
  );
}

function StreakBadge({ streak }: { streak: number }) {
  if (streak <= 0) {
    return (
      <span className="text-[11px] text-text-muted/70 uppercase tracking-wider">
        No streak yet
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-semibold tabular-nums">
      <Flame size={13} strokeWidth={ICON_STROKE} />
      {streak}
    </span>
  );
}
